import React, { useState } from 'react';
import styled from 'styled-components';
import { Tag, Plus, Edit, Trash2, Search, Clock, Percent, Calendar } from 'lucide-react';
import Sidebar from '../components/layout/Sidebar';
import Footer from '../components/layout/Footer';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import { Input, InputGroup } from '../components/common/Input';
import { theme } from '../styles/theme';

const Container = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: ${theme.colors.background};
`;

const MainContent = styled.main`
  flex: 1;
  margin-left: 260px;
  padding: ${theme.spacing.xl};
  padding-bottom: 80px;

  @media (max-width: ${theme.breakpoints.lg}) {
    margin-left: 0;
    padding: ${theme.spacing.md};
    padding-top: 70px;
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing.sm};
    padding-top: 70px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.xl};
  flex-wrap: wrap;
  gap: ${theme.spacing.md};
  
  h1 {
    font-size: ${theme.typography.fontSize['3xl']};
    font-weight: ${theme.typography.fontWeight.bold};
    color: ${theme.colors.text};
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    h1 {
      font-size: ${theme.typography.fontSize['2xl']};
    }
  }
`;

const Tabs = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.lg};
  border-bottom: 2px solid ${theme.colors.border};
  flex-wrap: wrap;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Tab = styled.button`
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border: none;
  background: transparent;
  border-bottom: 3px solid ${({ active }) => active ? theme.colors.primary : 'transparent'};
  color: ${({ active }) => active ? theme.colors.primary : theme.colors.textSecondary};
  font-weight: ${({ active }) => active ? theme.typography.fontWeight.semiBold : theme.typography.fontWeight.regular};
  font-family: ${theme.typography.fontFamily};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: ${theme.typography.fontSize.base};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  white-space: nowrap;

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing.sm} ${theme.spacing.md};
    font-size: ${theme.typography.fontSize.sm};
  }
`;

const PromotionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: ${theme.spacing.lg};

  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const PromotionCard = styled(Card)`
  padding: ${theme.spacing.lg};
  position: relative;
  border-left: 4px solid ${({ active }) => active ? theme.colors.status.ready : theme.colors.border};
`;

const PromotionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${theme.spacing.md};
`;

const PromotionInfo = styled.div`
  flex: 1;

  h3 {
    font-size: ${theme.typography.fontSize.xl};
    font-weight: ${theme.typography.fontWeight.bold};
    margin-bottom: ${theme.spacing.xs};
    color: ${theme.colors.text};
  }

  p {
    font-size: ${theme.typography.fontSize.sm};
    color: ${theme.colors.textSecondary};
    margin-bottom: ${theme.spacing.xs};
  }
`;

const DiscountBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.full};
  background-color: ${theme.colors.accent}20;
  color: ${theme.colors.accent};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semiBold};
`;

const StatusBadge = styled.span`
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.semiBold};
  background-color: ${({ active }) => active ? `${theme.colors.status.ready}20` : `${theme.colors.textSecondary}20`};
  color: ${({ active }) => active ? theme.colors.status.ready : theme.colors.textSecondary};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  margin-top: ${theme.spacing.md};
  flex-wrap: wrap;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${theme.zIndex.modal};
  padding: ${theme.spacing.lg};
`;

const ModalContent = styled(Card)`
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.lg};
  padding-bottom: ${theme.spacing.md};
  border-bottom: 2px solid ${theme.colors.border};

  h2 {
    font-size: ${theme.typography.fontSize.xl};
    font-weight: ${theme.typography.fontWeight.semiBold};
    color: ${theme.colors.text};
  }
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: ${theme.borderRadius.md};
  border: none;
  background-color: ${theme.colors.secondary};
  color: ${theme.colors.text};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${theme.colors.status.outOfStock};
    color: ${theme.colors.white};
  }
`;

const ModalForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  justify-content: flex-end;
  margin-top: ${theme.spacing.lg};
`;

// Mock data
const initialPromotions = [
  {
    id: 1,
    name: 'Happy Hour',
    type: 'happyHour',
    discount: 20,
    startTime: '17:00',
    endTime: '19:00',
    days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
    active: true,
  },
  {
    id: 2,
    name: 'Weekend Special',
    type: 'coupon',
    code: 'WEEKEND20',
    discount: 20,
    minOrder: 500,
    validFrom: '2024-12-20',
    validTo: '2024-12-31',
    active: true,
  },
  {
    id: 3,
    name: 'New Year Discount',
    type: 'campaign',
    discount: 15,
    description: 'Special discount for New Year',
    validFrom: '2024-12-25',
    validTo: '2025-01-05',
    active: false,
  },
];

function PromotionsManagement() {
  const [activeTab, setActiveTab] = useState('promotions');
  const [promotions, setPromotions] = useState(initialPromotions);
  const [showModal, setShowModal] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'coupon',
    discount: '',
    code: '',
    minOrder: '',
    startTime: '',
    endTime: '',
    days: [],
    validFrom: '',
    validTo: '',
    description: '',
    active: true,
  });

  const handleAdd = () => {
    setEditingPromotion(null);
    setFormData({
      name: '',
      type: 'coupon',
      discount: '',
      code: '',
      minOrder: '',
      startTime: '',
      endTime: '',
      days: [],
      validFrom: '',
      validTo: '',
      description: '',
      active: true,
    });
    setShowModal(true);
  };

  const handleEdit = (promotion) => {
    setEditingPromotion(promotion);
    setFormData(promotion);
    setShowModal(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this promotion?')) {
      setPromotions((prev) => prev.filter((p) => p.id !== id));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingPromotion) {
      setPromotions((prev) =>
        prev.map((p) => (p.id === editingPromotion.id ? { ...p, ...formData, discount: parseFloat(formData.discount) } : p))
      );
    } else {
      setPromotions((prev) => [...prev, { id: Date.now(), ...formData, discount: parseFloat(formData.discount) }]);
    }
    setShowModal(false);
  };

  const toggleActive = (id) => {
    setPromotions((prev) =>
      prev.map((p) => (p.id === id ? { ...p, active: !p.active } : p))
    );
  };

  return (
    <Container>
      <Sidebar />
      <MainContent>
        <Header>
          <h1>Promotions & Discounts</h1>
          <Button variant="primary" onClick={handleAdd}>
            <Plus size={20} />
            Add Promotion
          </Button>
        </Header>

        <Tabs>
          <Tab active={activeTab === 'promotions'} onClick={() => setActiveTab('promotions')}>
            <Tag size={18} />
            All Promotions
          </Tab>
          <Tab active={activeTab === 'happyHour'} onClick={() => setActiveTab('happyHour')}>
            <Clock size={18} />
            Happy Hour
          </Tab>
          <Tab active={activeTab === 'coupons'} onClick={() => setActiveTab('coupons')}>
            <Percent size={18} />
            Coupons
          </Tab>
          <Tab active={activeTab === 'campaigns'} onClick={() => setActiveTab('campaigns')}>
            <Calendar size={18} />
            Campaigns
          </Tab>
        </Tabs>

        {activeTab === 'promotions' && (
          <PromotionsGrid>
            {promotions.map((promotion) => (
              <PromotionCard key={promotion.id} active={promotion.active}>
                <PromotionHeader>
                  <PromotionInfo>
                    <h3>{promotion.name}</h3>
                    <DiscountBadge>
                      <Percent size={14} />
                      {promotion.discount}% OFF
                    </DiscountBadge>
                    <p style={{ marginTop: theme.spacing.xs }}>
                      {promotion.type === 'happyHour' && (
                        <>
                          <Clock size={12} style={{ marginRight: theme.spacing.xs, verticalAlign: 'middle' }} />
                          {promotion.startTime} - {promotion.endTime}
                        </>
                      )}
                      {promotion.type === 'coupon' && (
                        <>
                          Code: <strong>{promotion.code}</strong>
                        </>
                      )}
                      {promotion.type === 'campaign' && promotion.description}
                    </p>
                    {promotion.validFrom && (
                      <p style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.textSecondary }}>
                        <Calendar size={12} style={{ marginRight: theme.spacing.xs, verticalAlign: 'middle' }} />
                        {new Date(promotion.validFrom).toLocaleDateString()} - {new Date(promotion.validTo).toLocaleDateString()}
                      </p>
                    )}
                  </PromotionInfo>
                  <StatusBadge active={promotion.active}>
                    {promotion.active ? 'Active' : 'Inactive'}
                  </StatusBadge>
                </PromotionHeader>
                <ActionButtons>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => toggleActive(promotion.id)}
                  >
                    {promotion.active ? 'Deactivate' : 'Activate'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(promotion)}
                  >
                    <Edit size={16} />
                    Edit
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleDelete(promotion.id)}
                  >
                    <Trash2 size={16} />
                    Delete
                  </Button>
                </ActionButtons>
              </PromotionCard>
            ))}
          </PromotionsGrid>
        )}

        {activeTab === 'happyHour' && (
          <PromotionsGrid>
            {promotions.filter((p) => p.type === 'happyHour').map((promotion) => (
              <PromotionCard key={promotion.id} active={promotion.active}>
                <PromotionHeader>
                  <PromotionInfo>
                    <h3>{promotion.name}</h3>
                    <DiscountBadge>
                      <Percent size={14} />
                      {promotion.discount}% OFF
                    </DiscountBadge>
                    <p>
                      <Clock size={12} style={{ marginRight: theme.spacing.xs, verticalAlign: 'middle' }} />
                      {promotion.startTime} - {promotion.endTime}
                    </p>
                    <p style={{ fontSize: theme.typography.fontSize.xs }}>
                      Days: {promotion.days.join(', ')}
                    </p>
                  </PromotionInfo>
                </PromotionHeader>
                <ActionButtons>
                  <Button variant="outline" size="sm" onClick={() => handleEdit(promotion)}>
                    <Edit size={16} />
                    Edit
                  </Button>
                </ActionButtons>
              </PromotionCard>
            ))}
          </PromotionsGrid>
        )}

        {activeTab === 'coupons' && (
          <PromotionsGrid>
            {promotions.filter((p) => p.type === 'coupon').map((promotion) => (
              <PromotionCard key={promotion.id} active={promotion.active}>
                <PromotionHeader>
                  <PromotionInfo>
                    <h3>{promotion.name}</h3>
                    <DiscountBadge>
                      <Percent size={14} />
                      {promotion.discount}% OFF
                    </DiscountBadge>
                    <p>Code: <strong>{promotion.code}</strong></p>
                    {promotion.minOrder && (
                      <p style={{ fontSize: theme.typography.fontSize.xs }}>
                        Min Order: ₹{promotion.minOrder}
                      </p>
                    )}
                  </PromotionInfo>
                </PromotionHeader>
                <ActionButtons>
                  <Button variant="outline" size="sm" onClick={() => handleEdit(promotion)}>
                    <Edit size={16} />
                    Edit
                  </Button>
                </ActionButtons>
              </PromotionCard>
            ))}
          </PromotionsGrid>
        )}

        {activeTab === 'campaigns' && (
          <PromotionsGrid>
            {promotions.filter((p) => p.type === 'campaign').map((promotion) => (
              <PromotionCard key={promotion.id} active={promotion.active}>
                <PromotionHeader>
                  <PromotionInfo>
                    <h3>{promotion.name}</h3>
                    <DiscountBadge>
                      <Percent size={14} />
                      {promotion.discount}% OFF
                    </DiscountBadge>
                    <p>{promotion.description}</p>
                    <p style={{ fontSize: theme.typography.fontSize.xs }}>
                      <Calendar size={12} style={{ marginRight: theme.spacing.xs, verticalAlign: 'middle' }} />
                      {new Date(promotion.validFrom).toLocaleDateString()} - {new Date(promotion.validTo).toLocaleDateString()}
                    </p>
                  </PromotionInfo>
                </PromotionHeader>
                <ActionButtons>
                  <Button variant="outline" size="sm" onClick={() => handleEdit(promotion)}>
                    <Edit size={16} />
                    Edit
                  </Button>
                </ActionButtons>
              </PromotionCard>
            ))}
          </PromotionsGrid>
        )}

        {showModal && (
          <Modal onClick={() => setShowModal(false)}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <h2>{editingPromotion ? 'Edit Promotion' : 'Add Promotion'}</h2>
                <CloseButton onClick={() => setShowModal(false)}>×</CloseButton>
              </ModalHeader>
              <ModalForm onSubmit={handleSubmit}>
                <InputGroup>
                  <label>Promotion Name</label>
                  <Input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </InputGroup>
                <InputGroup>
                  <label>Type</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      fontSize: theme.typography.fontSize.base,
                      fontFamily: theme.typography.fontFamily,
                      border: `2px solid ${theme.colors.border}`,
                      borderRadius: theme.borderRadius.md,
                      backgroundColor: theme.colors.white,
                    }}
                    required
                  >
                    <option value="coupon">Coupon</option>
                    <option value="happyHour">Happy Hour</option>
                    <option value="campaign">Campaign</option>
                  </select>
                </InputGroup>
                <InputGroup>
                  <label>Discount (%)</label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.discount}
                    onChange={(e) => setFormData({ ...formData, discount: e.target.value })}
                    required
                  />
                </InputGroup>
                {formData.type === 'coupon' && (
                  <>
                    <InputGroup>
                      <label>Coupon Code</label>
                      <Input
                        type="text"
                        value={formData.code}
                        onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                        required
                      />
                    </InputGroup>
                    <InputGroup>
                      <label>Minimum Order (₹)</label>
                      <Input
                        type="number"
                        value={formData.minOrder}
                        onChange={(e) => setFormData({ ...formData, minOrder: e.target.value })}
                      />
                    </InputGroup>
                  </>
                )}
                {formData.type === 'happyHour' && (
                  <>
                    <InputGroup>
                      <label>Start Time</label>
                      <Input
                        type="time"
                        value={formData.startTime}
                        onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                        required
                      />
                    </InputGroup>
                    <InputGroup>
                      <label>End Time</label>
                      <Input
                        type="time"
                        value={formData.endTime}
                        onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                        required
                      />
                    </InputGroup>
                  </>
                )}
                {(formData.type === 'coupon' || formData.type === 'campaign') && (
                  <>
                    <InputGroup>
                      <label>Valid From</label>
                      <Input
                        type="date"
                        value={formData.validFrom}
                        onChange={(e) => setFormData({ ...formData, validFrom: e.target.value })}
                        required
                      />
                    </InputGroup>
                    <InputGroup>
                      <label>Valid To</label>
                      <Input
                        type="date"
                        value={formData.validTo}
                        onChange={(e) => setFormData({ ...formData, validTo: e.target.value })}
                        required
                      />
                    </InputGroup>
                  </>
                )}
                {formData.type === 'campaign' && (
                  <InputGroup>
                    <label>Description</label>
                    <Input
                      type="text"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    />
                  </InputGroup>
                )}
                <ButtonGroup>
                  <Button variant="secondary" type="button" onClick={() => setShowModal(false)}>
                    Cancel
                  </Button>
                  <Button variant="primary" type="submit">
                    {editingPromotion ? 'Update' : 'Add'} Promotion
                  </Button>
                </ButtonGroup>
              </ModalForm>
            </ModalContent>
          </Modal>
        )}
      </MainContent>
      <Footer />
    </Container>
  );
}

export default PromotionsManagement;

